import React, { useState, useEffect } from 'react';
import { produtosAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Package, Edit, Trash2, RefreshCw } from 'lucide-react';

const Produtos = () => {
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  
  const [formData, setFormData] = useState({
    nome: '',
    categoria: 'TV',
    ativo: true
  });

  useEffect(() => {
    loadProdutos();
  }, []);

  const loadProdutos = async () => {
    setLoading(true);
    try {
      const response = await produtosAPI.list();
      setProdutos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editando) {
        await produtosAPI.update(editando.id, formData);
        toast.success('Produto atualizado com sucesso!');
      } else {
        await produtosAPI.create(formData);
        toast.success('Produto criado com sucesso!');
      }
      
      setDialogOpen(false);
      setEditando(null);
      setFormData({ nome: '', categoria: 'TV', ativo: true });
      loadProdutos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (produto) => {
    setEditando(produto);
    setFormData({
      nome: produto.nome,
      categoria: produto.categoria,
      ativo: produto.ativo
    });
    setDialogOpen(true);
  };

  const handleDeletar = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este produto?')) return;

    try {
      await produtosAPI.delete(id);
      toast.success('Produto deletado com sucesso!');
      loadProdutos();
    } catch (error) {
      toast.error('Erro ao deletar produto');
    }
  };

  const handleNovo = () => {
    setEditando(null);
    setFormData({ nome: '', categoria: 'TV', ativo: true });
    setDialogOpen(true);
  };

  const categorias = ['TV', 'Internet', 'Fixo', 'Celular', 'Serviços Avançados', 'Ponto Adicional', 'La carte'];

  // Agrupar produtos por categoria
  const produtosPorCategoria = categorias.reduce((acc, cat) => {
    acc[cat] = produtos.filter(p => p.categoria === cat);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gerenciar Produtos</h1>
          <p className="text-gray-500 mt-1">Administre os produtos do sistema</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadProdutos} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Atualizar
          </Button>
          <Button onClick={handleNovo}>
            <Package className="mr-2 h-4 w-4" />
            Novo Produto
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Produtos Cadastrados: {produtos.length}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {categorias.map((categoria) => (
              <div key={categoria} className="border rounded-lg p-4">
                <h3 className="font-bold text-lg mb-3 text-gray-900">{categoria}</h3>
                <div className="space-y-2">
                  {produtosPorCategoria[categoria]?.length > 0 ? (
                    produtosPorCategoria[categoria].map((produto) => (
                      <div
                        key={produto.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{produto.nome}</p>
                          {!produto.ativo && (
                            <Badge className="bg-gray-100 text-gray-800 mt-1">Inativo</Badge>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditar(produto)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeletar(produto.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400 italic">Nenhum produto cadastrado</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar Produto' : 'Novo Produto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nome do Produto *</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Categoria *</Label>
              <select
                className="w-full border rounded-md px-3 py-2"
                value={formData.categoria}
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                required
              >
                {categorias.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ativo"
                checked={formData.ativo}
                onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
              />
              <Label htmlFor="ativo">Ativo</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Produtos;
